// Wordle-style emoji share text for a standard 5-letter board.
// This matches the same duplicate-letter logic used in `src/components/Grid.jsx`.

const EMOJI = {
  correct: '🟩',
  present: '🟨',
  absent: '⬛',
};

const SITE_URL = 'https://zoomed-in.com';

function normalizeGuess(guess) {
  return String(guess || '').trim().toUpperCase();
}

function buildStatusesForGuess(guess, solution) {
  const g = normalizeGuess(guess);
  const s = normalizeGuess(solution);
  const result = Array(5).fill('absent');

  // Count letters in solution that are NOT already matched correctly.
  const remaining = {};
  for (let i = 0; i < 5; i++) {
    const solLetter = s[i];
    const guessLetter = g[i];
    if (guessLetter && solLetter === guessLetter) {
      result[i] = 'correct';
    } else if (solLetter) {
      remaining[solLetter] = (remaining[solLetter] || 0) + 1;
    }
  }

  // Mark presents based on remaining counts.
  for (let i = 0; i < 5; i++) {
    if (result[i] === 'correct') continue;
    const letter = g[i];
    if (!letter) continue;
    if (remaining[letter] > 0) {
      result[i] = 'present';
      remaining[letter] -= 1;
    } else {
      result[i] = 'absent';
    }
  }

  return result;
}

/**
 * Returns an array of status rows for canvas rendering.
 * Each row is an array of 5 statuses: 'correct' | 'present' | 'absent'.
 */
export function buildStatusRows({ guesses, solution }) {
  const s = normalizeGuess(solution);
  if (!s || s.length < 5) return [];
  const rows = Array.isArray(guesses) ? guesses : [];

  return rows
    .map((g) => normalizeGuess(g))
    .filter((g) => g && g.length === 5)
    .map((g) => buildStatusesForGuess(g, s));
}

export function buildEmojiGrid({ guesses, solution }) {
  const s = normalizeGuess(solution);
  if (!s || s.length < 5) return '';
  const rows = Array.isArray(guesses) ? guesses : [];

  const filled = rows
    .map((g) => normalizeGuess(g))
    .filter((g) => g && g.length === 5);

  return filled
    .map((g) => buildStatusesForGuess(g, s).map((st) => EMOJI[st]).join(''))
    .join('\n');
}

/**
 * Build the full shareable results text.
 *
 * Output format:
 *   Zoomed In Generations
 *
 *   3/5
 *
 *   ⬛🟨⬛🟨🟨
 *   🟩🟩🟩⬛🟩
 *   🟩🟩🟩🟩🟩
 *
 *   Play free at https://zoomed-in.com
 */
export function buildShareText({ gameName, guesses, solution, maxGuesses = 5 }) {
  const s = normalizeGuess(solution);
  const rows = Array.isArray(guesses) ? guesses : [];
  const filled = rows
    .map((g) => normalizeGuess(g))
    .filter((g) => g && g.length === 5);

  const winIndex = filled.findIndex((g) => g === s);
  const score = winIndex >= 0 ? String(winIndex + 1) : 'X';

  const name = String(gameName || 'Game');
  const grid = buildEmojiGrid({ guesses, solution });
  if (!grid) return '';

  return `${name}\n\n${score}/${maxGuesses}\n\n${grid}\n\nPlay free at ${SITE_URL}`;
}
